import { create } from "zustand";
import proxy from "api/proxy";
import dates from "componentsv2/tools/dates";
import utils from "../services/appointment";

export const useContext = create((set) => ({
  context: {
    availableTime: [],
    configuration: null,
    owners: [],
    rooms: [],
    events: new Map(),
    currentOwner: null,
    currentDate: dates.startOfToday(),
    currentEvent: null,
    profile: null,
    shop: null,
    minStartTime: "00:00:00",
    maxEndTime: "23:59:59",
    schedule: [],
    enabledDays: [],
    maintenanceTypeIds: [],
  },
  updateContext: (newContext) => {
    set((state) => ({ context: { ...state.context, ...newContext } }));
  },
}));

export function isMaintenance(record) {
  if (record?.appointment_type?.maintenance === true) return true;
  const typeId = record?.appointment_type?.id ?? record?.appointment_type;
  const { maintenanceTypeIds } = useContext.getState().context;
  return !!typeId && (maintenanceTypeIds ?? []).includes(typeId);
}

export const setTimeData = async () => {
  const context = useContext.getState().context;
  const { owners, currentOwner, currentRoom, configuration } = context;
  const type = configuration?.[0]?.default_scheduling_mode;
  const { start, end } = utils.getStartAndEndDate(context);
  let ids = [];

  if (type !== "room" && !context.currentOwner && context.owners?.length) {
    useContext.setState((state) => ({
      context: {
        ...state.context,
        currentOwner: state.context.owners[0],
      },
    }));
  }
  if (type === "room") {
    ids = currentRoom ? [currentRoom.id] : [];
  } else {
    const owner = currentOwner || useContext.getState().context.currentOwner;
    ids = owner ? [owner.id] : owners.map((o) => o.id);
  }

  const args = {
    model: "appointment_scheduling.professional",
    method: "get_available_time",
    args: [
      {
        start_date: dates.fmt(start, "yyyy-MM-dd HH:mm:ss"),
        end_date: dates.fmt(end, "yyyy-MM-dd HH:mm:ss"),
        [type === "professional" ? "professionals" : "rooms"]: ids,
      },
    ],
  };
  const { data: availableTime } = await proxy.methodCall(args);
  const dom = [
    ["start_date", ">=", dates.fmt(start, "yyyy-MM-dd HH:mm:ss")],
    ["start_date", "<=", dates.fmt(end, "yyyy-MM-dd HH:mm:ss")],
    ["state", "!=", "cancelled"],
  ];
  if (ids.length) {
    dom.push([type === "professional" ? "professional" : "room", "in", ids]);
  }
  const order = [["start_date", "ASC"]];
  const { data: events_ } = await proxy.search(
    "appointment_scheduling",
    dom,
    [
      "eden_id",
      "appointment_date",
      "start_date",
      "end_date",
      "room",
      "room.rec_name",
      "professional",
      "professional.rec_name",
      "customer.rec_name",
      "patient.rec_name",
      "consumer.rec_name",
      "consumer.phone",
      "product.name",
      "product.type_mix",
      "product.quantity_mix_required",
      "product.time_session",
      "product.list_price",
      "product.sale_uom",
      "sale",
      "state",
      "number",
      "appointment_type",
      "appointment_type.name",
      "appointment_type.color",
      "description",
      "comment",
    ],
    null,
    order,
  );
  const events = utils.processDataEvents(events_);

  useContext.setState((state) => ({
    context: {
      ...state.context,
      availableTime: availableTime || [],
      events,
    },
  }));
};

export const initContext = async () => {
  const session = proxy.getSession();
  const { context } = useContext.getState();
  let owners = [];
  let rooms = [];
  let currentOwner = null;
  let response;
  let schedule;
  let minStartTime;
  let maxEndTime;
  let enabledDays;
  const { data: configuration } = await proxy.search(
    "appointment_scheduling.configuration",
    [],
    [
      "default_scheduling_mode",
      "schedule_days.day",
      "schedule_days.enable",
      "schedule_days.start_time",
      "schedule_days.end_time",
    ],
  );
  if (!context.profile && configuration[0].default_scheduling_mode !== "room") {
    const { data: agent } = await proxy.search(
      "commission.agent",
      [["user", "=", session.user]],
      [
        "party",
        "user.type_pos_user",
        "user.shop.schedule.day",
        "user.shop.schedule.enable",
        "user.shop.schedule.start_time",
        "user.shop.schedule.end_time",
      ],
    );

    if (agent?.length > 0) {
      const { data } = await proxy.search(
        "appointment_scheduling.professional",
        [["party", "=", agent[0].party]],
        ["party", "rec_name"],
      );
      if (data?.length > 0) {
        currentOwner = data[0];
      }
    }

    schedule = agent[0]["user."]["shop."]["schedule."];
    enabledDays = schedule.filter((d) => d.enable);
    minStartTime = enabledDays.reduce((min, d) => {
      return d.start_time && (!min || d.start_time < min) ? d.start_time : min;
    }, null);
    maxEndTime = enabledDays.reduce((max, d) => {
      return d.end_time && (!max || d.end_time > max) ? d.end_time : max;
    }, null);
    const profile = agent[0]["user."].type_pos_user;
    if (["frontend_admin", "cashier"].includes(profile)) {
      response = await proxy.search(
        "appointment_scheduling.professional",
        [["party", "!=", null]],
        ["party", "rec_name"],
      );

      owners = response?.data?.map((item) => ({
        id: item.id,
        name: item.rec_name,
      }));
    }
  } else {
    schedule = configuration[0]["schedule_days."];
    enabledDays = schedule.filter((d) => d.enable);
    minStartTime = enabledDays.reduce((min, d) => {
      return d.start_time && (!min || d.start_time < min) ? d.start_time : min;
    }, null);
    maxEndTime = enabledDays.reduce((max, d) => {
      return d.end_time && (!max || d.end_time > max) ? d.end_time : max;
    }, null);
    response = await proxy.search(
      "appointment_scheduling.room",
      [["name", "!=", null]],
      ["name", "rec_name"],
    );

    rooms = response?.data?.map((item) => ({
      id: item.id,
      name: item.rec_name,
    }));
  }

  const { data: maintenanceTypes } = await proxy.search(
    "appointment_scheduling.appointment_type",
    [["maintenance", "=", true]],
    ["id"],
  );

  useContext.setState({
    context: {
      ...context,
      maintenanceTypeIds: maintenanceTypes?.map((t) => t.id) ?? [],
      owners,
      rooms,
      configuration,
      minStartTime,
      maxEndTime,
      currentOwner,
      enabledDays,
      schedule,
      shop: session.shop,
      edenAlert: null,
    },
  });
};
